import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import DramaCard from '@/components/base/DramaCard';
import Pagination from '@/components/base/Pagination';
import FilterBar, { type FilterState } from './components/FilterBar';
import { filterDramas, sortDramas, type SortKey } from '@/utils/drama';

const PAGE_SIZE = 8;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialGenre = searchParams.get('genre') || '';
  const initialSort = (searchParams.get('sort') as SortKey) || 'update';

  const [filter, setFilter] = useState<FilterState>({
    genre: initialGenre,
    region: '',
    year: '',
    sort: initialSort,
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const results = useMemo(() => {
    const filtered = filterDramas({
      genre: filter.genre,
      region: filter.region,
      year: filter.year,
    });
    return sortDramas(filtered, filter.sort);
  }, [filter]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleReset = () => {
    setFilter({ genre: '', region: '', year: '', sort: 'update' });
    setSearchParams({});
  };

  return (
    <SiteLayout>
      <title>电视剧分类 - 善良的小姨子在线</title>
      <meta
        name="description"
        content="善良的小姨子在线电视剧分类页，支持按类型、地区、年份筛选，按更新时间、新剧优先与热度排序，快速找到想追的剧。"
      />
      <meta name="keywords" content="电视剧分类,电视剧筛选,追剧平台,善良的小姨子在线" />

      <div className="mx-auto max-w-6xl px-4 pt-6 md:px-6">
        <nav className="flex items-center gap-1 text-sm text-foreground-600" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-900">电视剧分类</span>
        </nav>

        <div className="mt-4 mb-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            电视剧分类
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            按类型、地区、年份筛选你想看的剧集，支持多种排序方式，快速找到下一部追的剧。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 pb-4 md:px-6">
        <FilterBar
          filter={filter}
          onChange={setFilter}
          onReset={handleReset}
          resultCount={results.length}
        />
      </div>

      <div className="mx-auto max-w-6xl px-4 pb-4 md:px-6">
        <p className="mb-4 text-sm text-foreground-600">
          共找到 <span className="font-semibold text-primary-600">{results.length}</span> 部符合条件的剧集
        </p>
      </div>

      <div className="mx-auto max-w-6xl px-4 md:px-6">
        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
              {pageItems.map((drama, i) => (
                <DramaCard key={drama.id} drama={drama} index={i} />
              ))}
            </div>
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
              没有找到符合条件的剧集
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              试着放宽筛选条件，或者重置后重新选择类型、地区与年份。
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-5 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}